import { useCallback, useState, type ChangeEvent, type ReactNode } from 'react';
import { EMP_LEVELS } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { DataTable, type Column } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { StatCards } from '@/components/StatCards';
import { useToast } from '@/components/Toast';
import { ConfirmDialog } from './ConfirmDialog';
import { downloadWorkbook, readFirstSheet, type CellValue } from './sheets';

/** Column headings of the organization template, in order. */
export const ORG_TEMPLATE_HEADERS = [
  'Employee ID',
  'First Name',
  'Last Name',
  'Company Email',
  'Department',
  'Position',
  'Employment Level',
  'Reports To (email)',
] as const;

export interface ImportRow {
  empId: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  position: string;
  employmentLevel: string;
  managerEmail: string;
}

interface RowError {
  row: number;
  email: string;
  errors: string[];
}

interface BulkResult {
  imported: number;
  validCount?: number;
  linked?: number;
  errors: RowError[];
}

function cell(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

function toImportRows(raw: Array<Record<string, unknown>>): ImportRow[] {
  return raw.map((r) => ({
    empId: cell(r, 'Employee ID', 'empId'),
    firstName: cell(r, 'First Name', 'firstName'),
    lastName: cell(r, 'Last Name', 'lastName'),
    email: cell(r, 'Company Email', 'email').toLowerCase(),
    department: cell(r, 'Department', 'department'),
    position: cell(r, 'Position', 'position'),
    employmentLevel: cell(r, 'Employment Level', 'employmentLevel'),
    managerEmail: cell(r, 'Reports To (email)', 'Reports To', 'managerEmail').toLowerCase(),
  }));
}

export function orgTemplateRows(): CellValue[][] {
  return [
    [...ORG_TEMPLATE_HEADERS],
    [
      'GDEC-0099',
      'Juan',
      'Dela Cruz',
      'juan.delacruz@gdec.com',
      'Commercial',
      'Associate',
      EMP_LEVELS[0],
      'manager@gdec.com',
    ],
  ];
}

export interface BulkUploadModalProps {
  onClose: () => void;
  onChanged: () => Promise<void>;
}

/**
 * Bulk import from the organization spreadsheet.
 *
 * Always dry-runs first. The server writes nothing unless every row is valid,
 * so offering "Import" while errors remain would promise something it will not
 * do — the button only appears once the file is clean.
 */
export function BulkUploadModal({ onClose, onChanged }: BulkUploadModalProps): ReactNode {
  const { toast } = useToast();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const onFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = e.target.files?.[0];
      if (!file) return;
      setFileName(file.name);
      setRows(null);
      setResult(null);
      setError(null);
      setBusy(true);
      try {
        const parsed = toImportRows(await readFirstSheet(file));
        if (parsed.length === 0) {
          setError('That file has no data rows. Use the template columns.');
          setBusy(false);
          return;
        }
        setRows(parsed);
        const dry = await api.admin.bulkImportUsers<BulkResult>({ rows: parsed, dryRun: true });
        setResult(dry);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const runImport = useCallback(async (): Promise<void> => {
    if (!rows) return;
    const final = await api.admin.bulkImportUsers<BulkResult>({ rows, dryRun: false });
    await onChanged();
    toast(
      `Imported ${final.imported} employee(s), ${final.linked ?? 0} reporting line(s) set.`,
      'ok',
    );
    setConfirming(false);
    onClose();
  }, [onChanged, onClose, rows, toast]);

  const downloadErrors = useCallback((): void => {
    if (!result) return;
    void downloadWorkbook('GDEC_Bulk_Upload_Errors.xlsx', [
      {
        name: 'Errors',
        rows: [
          ['Row', 'Email', 'Errors'],
          ...result.errors.map((r): CellValue[] => [r.row, r.email, r.errors.join('; ')]),
        ],
      },
    ]);
  }, [result]);

  const errorColumns: ReadonlyArray<Column<RowError>> = [
    { key: 'row', header: 'Row', cell: (r) => r.row, width: '60px' },
    { key: 'email', header: 'Email', cell: (r) => r.email || '—' },
    {
      key: 'errors',
      header: 'Errors',
      cell: (r) => <span style={{ color: 'var(--bad)' }}>{r.errors.join(', ')}</span>,
    },
  ];

  const total = rows?.length ?? 0;
  const invalid = result?.errors.length ?? 0;
  const clean = result !== null && invalid === 0 && total > 0;

  return (
    <>
      <Modal title="Bulk Upload Organization" onClose={busy ? () => undefined : onClose} wide>
        <p className="panel-note">
          Upload an .xlsx or .csv file using the template columns. Every row is validated first and
          nothing is written unless all of them pass.
        </p>
        <div className="field">
          <label htmlFor="bulk-file">Organization file</label>
          <input id="bulk-file" type="file" accept=".xlsx,.csv" onChange={(e) => void onFile(e)} />
          {fileName ? <p className="field-note">{fileName}</p> : null}
        </div>

        {busy ? <Spinner /> : null}
        {error ? <div className="error-text">{error}</div> : null}

        {result ? (
          <>
            <StatCards
              cards={[
                { n: total, l: 'Rows In File' },
                { n: result.validCount ?? total - invalid, l: 'Valid Rows', accent: true },
                { n: invalid, l: 'Rows With Errors' },
              ]}
            />
            {invalid > 0 ? (
              <>
                <div className="scroll-y" style={{ maxHeight: 240 }}>
                  <DataTable
                    rows={result.errors}
                    columns={errorColumns}
                    rowKey={(r) => String(r.row)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={downloadErrors}
                >
                  Download Error Report
                </button>
                <p className="panel-note" style={{ marginTop: 10 }}>
                  Fix these rows and upload the file again. The server will not import a file with
                  any invalid row.
                </p>
              </>
            ) : (
              <p className="panel-note">All {total} row(s) passed validation.</p>
            )}
          </>
        ) : null}

        <div className="cf-submitbar">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setConfirming(true)}
            disabled={!clean || busy}
          >
            Import {total} Employee(s)
          </button>
        </div>
      </Modal>

      {confirming ? (
        <ConfirmDialog
          title="Confirm Bulk Import"
          confirmLabel={`Import ${total} employee(s)`}
          onCancel={() => setConfirming(false)}
          onConfirm={runImport}
        >
          <p>
            This creates <b>{total}</b> new employee record(s) from <b>{fileName}</b>, each with
            Requestor access, and sets their reporting lines from the &quot;Reports To (email)&quot;
            column.
          </p>
          <p>Existing employees are not modified. New accounts cannot sign in until invited.</p>
        </ConfirmDialog>
      ) : null}
    </>
  );
}
